// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { invoiceDetailSchema } from '@/billing/api/types';
import { Currency } from '@/currency/api/types';
import {
  serializeCurrencyForAPI,
  serializeCurrencyForAPINullable,
} from '@/currency/utilities/serialize';
import { z } from 'zod';

const updateLineItemsResponseSchema = z.object({
  data: invoiceDetailSchema,
  metadata: responseMetadataSchema,
});

export interface UpdateLineItemEntry {
  key: string | null;
  pendingChargeKey: string | null;
  billingEligibleDate: string | null;
  description: string;
  pricingMode: string;
  quantity: number;
  unitPrice: Currency | null;
  totalPrice: Currency;
  taxAmount: Currency | null;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  sequence: number;
}

export interface NewLineItemEntry {
  itemKey: string;
  locationKey: string | null;
  chargeLabel: string;
  pricingMode: string;
  quantity: number;
  unitPrice: Currency | null;
  totalPrice: Currency;
  taxAmount: Currency | null;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  expectedBillableDate: string | null;
  sequence: number;
}

/** Serializes a line item's unit price, total price and tax for the request body. */
function serializeLineItemAmounts<
  T extends UpdateLineItemEntry | NewLineItemEntry,
>(lineItem: T) {
  return {
    ...lineItem,
    unitPrice: serializeCurrencyForAPINullable(lineItem.unitPrice),
    totalPrice: serializeCurrencyForAPI(lineItem.totalPrice),
    taxAmount: serializeCurrencyForAPINullable(lineItem.taxAmount),
  };
}

export interface UpdateLineItemsInput {
  invoiceKey: string;
  lineItems: UpdateLineItemEntry[];
  newLineItems: NewLineItemEntry[];
}

export async function updateLineItems(input: UpdateLineItemsInput) {
  const url = `/v1/invoice/${input.invoiceKey}/line-items`;
  const response = await api.patch(url, {
    lineItems: input.lineItems.map(serializeLineItemAmounts),
    newLineItems: input.newLineItems.map(serializeLineItemAmounts),
  });

  const result = await parseResponse(
    updateLineItemsResponseSchema,
    response.data,
    {
      schemaName: 'UpdateLineItems',
      endpoint: url,
    }
  );
  return result.data;
}
